import { useEffect, useMemo, useState } from "react";
import { FleetVehicleExpenseStats, FleetVehicleExpenseWeek } from "services/api/fleet/vehicles/expenses/types";
import { Loader } from "ui/components/Loader";
import VehicleExpenseChart, { ExpenseChartBucket } from "../VehicleExpenseChart/VehicleExpenseChart";
import VehicleExpenseStatsHeader from "../VehicleExpenseStatsHeader/VehicleExpenseStatsHeader";

interface VehicleExpenseWeeklyProps {
    fetchStats: () => Promise<FleetVehicleExpenseStats>;
    title?: string;
    subtitle?: string;
}

const parseWeekNumber = (wYyyy: string): { week: number; year: number } => {
    const [w, y] = wYyyy.split("/").map(Number);
    return { week: w || 0, year: y || 0 };
};

// Prikaži samo neki broj labela ako ima previše tjedana, da se ne preklapaju
// (labela je sada dvodatni raspon "07.04.–13.04." pa treba više prostora)
const labelStride = (count: number): number => {
    if (count <= 8) return 1;
    if (count <= 16) return 2;
    if (count <= 30) return 3;
    if (count <= 60) return 6;
    return Math.ceil(count / 10);
};

const weekStartDate = (w: FleetVehicleExpenseWeek): Date => {
    return typeof w.startDate === "string" ? new Date(w.startDate) : w.startDate;
};

const emptyStats: FleetVehicleExpenseStats = {
    totalAmount: 0,
    months: [],
    weeks: [],
    sellingPriceHistory: [],
};

const VehicleExpenseWeekly: React.FC<VehicleExpenseWeeklyProps> = ({ fetchStats, title, subtitle }) => {
    const [stats, setStats] = useState<FleetVehicleExpenseStats | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            try {
                const result = await fetchStats();
                if (!cancelled) setStats(result);
            } catch {
                if (!cancelled) setStats(emptyStats);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [fetchStats]);

    const data = stats ?? emptyStats;
    const weeks = data.weeks ?? [];

    const fmtShort = (d: Date) =>
        `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;

    const buckets: ExpenseChartBucket[] = useMemo(() => {
        const stride = labelStride(weeks.length);
        return weeks.map((w, i) => {
            const showLabel = i % stride === 0 || i === weeks.length - 1;
            const start = weekStartDate(w);
            const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
            return {
                label: showLabel ? `${fmtShort(start)}–${fmtShort(end)}` : "",
                items: w.items ?? [],
                cumulative: w.amount ?? 0,
            };
        });
    }, [weeks]);

    const { windowStart, windowEndDate } = useMemo(() => {
        if (weeks.length === 0) {
            const now = new Date();
            return { windowStart: now, windowEndDate: now };
        }
        const first = weekStartDate(weeks[0]);
        const last = weekStartDate(weeks[weeks.length - 1]);
        return {
            windowStart: first,
            windowEndDate: new Date(last.getTime() + 7 * 24 * 60 * 60 * 1000),
        };
    }, [weeks]);

    const tooltipTitle = (b: ExpenseChartBucket) => {
        const idx = buckets.indexOf(b);
        const w = weeks[idx];
        if (!w) return b.label;
        const parsed = parseWeekNumber(w.week);
        const start = weekStartDate(w);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        const fmt = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
        return `Tjedan ${parsed.week}/${parsed.year} (${fmt(start)} – ${fmt(end)})`;
    };

    if (isLoading || !stats) {
        return (
            <div className="relative flex-1 h-full">
                <Loader isLoading={true}>{null}</Loader>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full pt-4">
            <VehicleExpenseStatsHeader stats={data} title={title} subtitle={subtitle ?? "Kumulativni pregled po tjednima do trenutnog tjedna"} />
            <VehicleExpenseChart
                buckets={buckets}
                windowStart={windowStart}
                windowEndDate={windowEndDate}
                priceHistory={data.sellingPriceHistory ?? []}
                bucketTooltipTitle={tooltipTitle}
            />
        </div>
    );
};

export default VehicleExpenseWeekly;
